import { useEffect, useMemo, useRef } from 'react';
import { clearTimeout, setTimeout } from 'worker-timers';

import type { QueryActionCreatorResult } from '@reduxjs/toolkit/query';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefetchFn = () => QueryActionCreatorResult<any>;

interface UsePollingQueryOptions {
  getDelay?: () => number;
  baseRetryDelay?: number;
  maxRetryDelay?: number;
  skip?: boolean;
}

interface PollingQueryExtras<TData> {
  data: TData | undefined;
  lastSentTime: Date | null;
  lastReceiveTime: Date | null;
}

interface RTKQueryResult<TData = unknown> {
  data?: TData;
  currentData?: TData;
  refetch: RefetchFn;
  startedTimeStamp?: number;
  fulfilledTimeStamp?: number;
}

// Extract TData from RTKQueryResult
type ExtractData<T> = T extends RTKQueryResult<infer D> ? D : never;

const DEFAULT_BASE_RETRY_DELAY = 1_000;
const DEFAULT_MAX_RETRY_DELAY = 30_000;

export const usePollingQuery = <TQuery extends RTKQueryResult>(
  queryResult: TQuery,
  options: UsePollingQueryOptions = {},
): Omit<TQuery, 'data' | 'currentData'> &
  PollingQueryExtras<ExtractData<TQuery>> => {
  const {
    getDelay,
    baseRetryDelay = DEFAULT_BASE_RETRY_DELAY,
    maxRetryDelay = DEFAULT_MAX_RETRY_DELAY,
    skip = false,
  } = options;

  const timeoutRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const refetchRef = useRef(queryResult.refetch);
  refetchRef.current = queryResult.refetch;

  useEffect(() => {
    if (skip) {
      return;
    }

    let isMounted = true;

    const clearScheduledPoll = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const getBackoffDelay = () =>
      Math.min(baseRetryDelay * 2 ** retryCountRef.current, maxRetryDelay);

    const scheduleNextPoll = (delayOverride?: number) => {
      clearScheduledPoll();

      const delay = delayOverride ?? getDelay?.() ?? DEFAULT_BASE_RETRY_DELAY;

      timeoutRef.current = setTimeout(async () => {
        const { error } = await refetchRef.current();

        if (!isMounted) {
          return;
        }

        if (error) {
          retryCountRef.current += 1;
          scheduleNextPoll(getBackoffDelay());
        } else {
          retryCountRef.current = 0;
          scheduleNextPoll();
        }
      }, delay);
    };

    scheduleNextPoll();

    return () => {
      isMounted = false;
      clearScheduledPoll();
    };
  }, [skip, getDelay, baseRetryDelay, maxRetryDelay]);

  const lastSentTime = useMemo(
    () =>
      queryResult.startedTimeStamp
        ? new Date(queryResult.startedTimeStamp)
        : null,
    [queryResult.startedTimeStamp],
  );

  const lastReceiveTime = useMemo(
    () =>
      queryResult.fulfilledTimeStamp
        ? new Date(queryResult.fulfilledTimeStamp)
        : null,
    [queryResult.fulfilledTimeStamp],
  );

  // Destructure only what we're replacing
  const { data: newData, currentData, ...rest } = queryResult;

  return {
    ...rest,
    data: (newData ?? currentData) as ExtractData<TQuery> | undefined,
    lastSentTime,
    lastReceiveTime,
  };
};
